/**
 * ADMIN ONLY — SelleSend revenue by source (Mike, 10-oct: "We need to add all
 * profits and sources of revenue"). Ver backend/services/ingresos.py: cada
 * fuente se cuenta una vez, y la caja va aparte porque no es ingreso.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DollarSign, Loader2, TrendingUp } from 'lucide-react';
import { fetchJson, formatMoney as money } from '@/lib/api';

const PERIODOS = [[7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [365, 'Last 12 months']];

const Fila = ({ label, value, strong, muted }) => (
  <div className={`flex justify-between gap-4 py-1 ${muted ? 'text-slate-500' : ''}`}>
    <span>{label}</span>
    <span className={`tabular-nums ${strong ? 'font-semibold text-emerald-700' : ''}`}>{value}</span>
  </div>
);

const Fuente = ({ titulo, children, testid }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-sm" data-testid={testid}>
    <p className="font-semibold mb-2">{titulo}</p>
    {children}
  </div>
);

export default function AdminRevenuePage() {
  const { token } = useAuth();
  const [dias, setDias] = useState(30);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  // Cada carga lleva su numero: si se cambia de periodo antes de que llegue la
  // anterior, la respuesta vieja se descarta. Antes podian verse los numeros
  // de 30 dias bajo "Last 12 months".
  const ultima = useRef(0);
  const cargar = useCallback(async () => {
    const esta = ++ultima.current;
    setError(null); setD(null);
    try {
      const j = await fetchJson(`/admin/revenue?days=${dias}`, { token });
      if (esta !== ultima.current) return;
      setD(j);
    } catch (e) { if (esta === ultima.current) setError(e.message); }
  }, [dias, token]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><TrendingUp className="w-6 h-6 text-emerald-600" /> Revenue</h1>
          <p className="text-sm text-slate-500">What SelleSend billed and earned, by source. Each charge is counted once.</p>
        </div>
        <select className="input" value={dias} onChange={(e) => setDias(Number(e.target.value))} data-testid="revenue-period">
          {PERIODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {error && <p className="text-sm text-rose-700">{error}</p>}
      {!d && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}

      {d && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl p-5 bg-slate-900 text-white" data-testid="revenue-billed">
              <p className="text-xs uppercase tracking-wide text-slate-400">Billed to merchants</p>
              <p className="text-3xl font-bold tabular-nums mt-1">{money(d.total_billed)}</p>
            </div>
            <div className="rounded-xl p-5 bg-gradient-to-br from-emerald-600 to-teal-600 text-white" data-testid="revenue-margin">
              <p className="text-xs uppercase tracking-wide text-emerald-100">SelleSend margin</p>
              <p className="text-3xl font-bold tabular-nums mt-1">{money(d.total_margin)}</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Fuente titulo={`Labels · Rate Shop (${d.labels.count})`} testid="revenue-labels">
              <Fila label="Shipping charged" value={money(d.labels.billed)} />
              <Fila label="Carrier cost" value={money(d.labels.carrier_cost)} muted />
              <Fila label="Margin" value={money(d.labels.margin)} strong />
            </Fuente>
            <Fuente titulo={`Coverage (${d.coverage.count})`} testid="revenue-coverage">
              <Fila label="Charged to merchants" value={money(d.coverage.charged)} />
              <Fila label="Pure premium (insurer)" value={money(d.coverage.insurer_premium)} muted />
              <Fila label="Margin" value={money(d.coverage.margin)} strong />
              {d.coverage.not_charged_count > 0 && (
                <p className="text-xs text-slate-500 mt-1">{d.coverage.not_charged_count} coverage(s) issued before coverage was charged.</p>
              )}
            </Fuente>
            <Fuente titulo={`SelleGard · WebShip (${d.sellegard.count})`} testid="revenue-sellegard">
              <Fila label="Billable (shipping)" value={money(d.sellegard.billed)} />
              <Fila label="WebShip cost" value={money(d.sellegard.carrier_cost)} muted />
              <Fila label="Extra margin" value={money(d.sellegard.margin)} strong />
              <Fila label="On an invoice" value={money(d.sellegard.invoiced)} muted />
              <Fila label="Paid" value={money(d.sellegard.paid)} muted />
              {d.sellegard.recorded_not_billable > 0 && (
                <p className="text-xs text-slate-500 mt-1">{d.sellegard.recorded_not_billable} shipment(s) recorded but not billable (before the billing date, unassigned or without a cost).</p>
              )}
            </Fuente>
            {d.admin_fees && (
              <Fuente titulo={`Admin fees (${d.admin_fees.count})`} testid="revenue-admin-fees">
                <Fila label="Rate Shop labels" value={money(d.admin_fees.labels)} />
                <Fila label="SelleGard · WebShip" value={money(d.admin_fees.sellegard)} />
                <Fila label="Margin" value={money(d.admin_fees.charged)} strong />
              </Fuente>
            )}
            {d.merchant_payments && (
              <Fuente titulo={`SelleCheck · payments between merchants (${d.merchant_payments.count})`} testid="revenue-merchant-payments">
                <Fila label="Paid by buyers (goes to sellers)" value={money(d.merchant_payments.volume)} muted />
                <Fila label="SelleSend fees" value={money(d.merchant_payments.fees)} strong />
                <Fila label="Refunded to buyers" value={money(d.merchant_payments.refunded)} muted />
                <Fila label="Lost disputes paid back (guarantee)" value={money(d.merchant_payments.disputes_absorbed)} muted />
                <p className="text-xs text-slate-500 mt-1">Stripe takes its card cost out of the fee; that cost is not shown here.</p>
              </Fuente>
            )}
            {d.bank_checks && (
              <Fuente titulo={`SelleCheck · bank checks (${d.bank_checks.count})`} testid="revenue-bank-checks">
                <Fila label="Sent by bank (goes to the receiving merchant)" value={money(d.bank_checks.volume)} muted />
                <Fila label="SelleSend fees" value={money(d.bank_checks.fees)} />
                <Fila label="Stripe bank verification and balance checks" value={`− ${money(d.bank_checks.stripe_costs)}`} muted />
                <Fila label="Margin" value={money(d.bank_checks.fees - d.bank_checks.stripe_costs)} strong />
                <p className="text-xs text-slate-500 mt-1">Stripe list prices: 3.10 per bank connected, 0.10 per balance check. The ACH fee is paid by the receiving merchant.</p>
              </Fuente>
            )}
            {d.photos && (
              <Fuente titulo={`Item photos (${d.photos.count})`} testid="revenue-photos">
                <Fila label="Charged, once per shipment" value={money(d.photos.charged)} strong />
                {d.photos.waived > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{d.photos.waived} shipment photo(s) waived by the merchant setup.</p>
                )}
              </Fuente>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Fuente titulo="Money received" testid="revenue-collected">
              <Fila label="Wallet top-ups" value={money(d.collected.wallet_topups)} />
              <Fila label={`Credit invoices paid (${d.collected.invoices_paid_count})`} value={money(d.collected.invoices_paid)} />
              <p className="text-xs text-slate-500 mt-1">Cash in, not revenue: top-ups are spent on labels and coverage above.</p>
            </Fuente>
            <Fuente titulo="Merchants by margin" testid="revenue-top">
              {d.top_merchants.length === 0 ? <p className="text-slate-500">No margin in this period.</p>
                : d.top_merchants.map((m) => <Fila key={m.merchant} label={m.merchant} value={money(m.margin)} />)}
            </Fuente>
          </div>

          {d.programs.count > 0 && (
            <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="revenue-programs">
              <DollarSign className="w-3 h-3" /> {d.programs.count} protection program fee(s) recorded for {money(d.programs.recorded)}, never charged to the merchant.
            </p>
          )}
          <p className="text-xs text-slate-400">Surcharges and monthly plans will appear here as their own sources once they exist.</p>
        </>
      )}
    </div>
  );
}
